import React from "react";
import { formatDateString } from "../../utils/dates";
import { Icon } from "../Common/Icons/LucideIcons";
import { Body } from "../Common/Typography";
import type { Task } from "./__queries__";
import {
  LinkedTaskContainer,
  LinkedTaskItem,
  LinkedTaskItemContent,
  LinkedTaskItemLink,
} from "./__styles__/LinkedTask";
import { TaskModalSubmission, useTaskModal } from "./Form";

type TaskAssignee = {
  id: string;
  firstName?: string;
  lastName?: string;
};

type Props = {
  tasks: Task[];
  submission: TaskModalSubmission;
};

const getAssigneeNames = (assignees?: TaskAssignee[]): string | null => {
  if (!assignees || assignees.length === 0) {
    return null;
  }

  return assignees
    .map(assignee => `${assignee?.firstName || ""} ${assignee?.lastName || ""}`)
    .join(", ");
};

const getAssigneeCreatedAtInfo = (
  createdAt: string,
  assignees?: TaskAssignee[]
): string | null => {
  const assigneeNames = getAssigneeNames(assignees);
  const formattedDate = formatDateString({
    dateString: createdAt,
    format: "MM/DD/YYYY",
  });

  return [
    assigneeNames && `Assigned to: ${assigneeNames}`,
    `Created: ${formattedDate}`,
  ]
    .filter(Boolean)
    .join(", ");
};

function LinkedTaskItemRow({
  task,
  submission,
}: {
  task: Task;
  submission: Props["submission"];
}) {
  const assigneeCreatedAtInfo = getAssigneeCreatedAtInfo(
    task.createdAt,
    task.assignees
  );

  const [showTaskModal, hideTaskModal] = useTaskModal({
    targetObject: submission?.property,
    existingTask: task,
    submission: submission,
    onSubmit: () => {
      hideTaskModal();
    },
    onCancel: () => {
      hideTaskModal();
    },
  });

  return (
    <LinkedTaskItem>
      <Icon
        iconName="circle-alert"
        color="contentInfo"
        size={16}
        style={{ marginTop: 2 }}
      />
      <LinkedTaskItemContent>
        <Body size="default" type="emphasis">
          Linked task:
          <LinkedTaskItemLink onClick={() => showTaskModal()}>
            {task.name}
          </LinkedTaskItemLink>
        </Body>
        {assigneeCreatedAtInfo && (
          <Body size="default" type="regular">
            {assigneeCreatedAtInfo}
          </Body>
        )}
      </LinkedTaskItemContent>
    </LinkedTaskItem>
  );
}

export function LinkedTask({ tasks, submission }: Props) {
  if (!tasks || tasks.length === 0) {
    return null;
  }

  return (
    <LinkedTaskContainer>
      {tasks.map(task => (
        <LinkedTaskItemRow key={task.id} task={task} submission={submission} />
      ))}
    </LinkedTaskContainer>
  );
}
